import React, { useRef } from 'react'
import { Card, Col, Input, List, Row } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { RequestTicket } from 'src/types'

import { ApplicationState } from 'src/store'
import { currencyMask } from 'src/components/Formik'
import { setFullRequest } from 'src/core/store/ducks/requests/actions'
import { TagTicketSituation } from 'src/components/molecules'
import { Text, Title } from 'src/components/Ant'
import UserAvatar from 'src/components/UserAvatar'

import {
  AirTravelerRow,
  AirTravelerRowInfo,
  AirTravelerRowName
} from './styles'
import QuoteAirItem from './quote-air-item'

import { UpdateETicketCodeRequest } from '../util'

type Props = {
  item: RequestTicket
  canEdit: boolean
}

function TicketItem({ canEdit, item }: Props) {
  const { requests } = useSelector((state: ApplicationState) => ({
    requests: state.requests
  }))

  const { t } = useTranslation()
  const refTimeout = useRef<NodeJS.Timeout>(null)
  const dispatch = useDispatch()

  const booking_expiration = moment
    .unix(
      item.request_ticket_air_selected.request_quote_air_trip.booking_expiration
    )
    .utc()
    .format('DD/MM/YYYY HH:mm')
  const situation = item?.request_ticket_air_selected?.situation
  const { request_standard_sharings } =
    requests.fullRequest?.request_standard_assignments

  return (
    <List.Item>
      <Card
        title={
          <Row gutter={24}>
            <Col
              span={8}
              style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              <Text
                copyable={{
                  text: item.request_ticket_air_selected.request_quote_air_trip
                    .booking_code
                }}
                type="secondary"
              >
                <Text strong type="secondary">
                  Localizador:
                </Text>{' '}
                {
                  item.request_ticket_air_selected.request_quote_air_trip
                    .booking_code
                }
              </Text>
              <Text type="secondary">
                <Text strong type="secondary">
                  Consolidadora:{' '}
                </Text>
                <Text type="secondary">Reserva Fácil</Text>
              </Text>
            </Col>
            <Col
              span={8}
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              {!!situation && <TagTicketSituation status={situation} />}
              {['APPROVING', 'RESERVED'].includes(situation) && (
                <Text type="secondary">
                  <Text strong type="secondary">
                    Prazo:
                  </Text>{' '}
                  {booking_expiration}
                </Text>
              )}
            </Col>
            <Col
              span={8}
              style={{
                alignItems: 'flex-end',
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              {situation === 'ISSUED' && (
                <Text
                  code
                  copyable={{
                    text: item.request_ticket_air_selected.eticket_code
                  }}
                  style={{ fontSize: '16px' }}
                >
                  {item.request_ticket_air_selected.eticket_code}
                </Text>
              )}
              {item.status === 'EMISSION_PENDING' && (
                <Input
                  disabled={!canEdit}
                  size="small"
                  placeholder="Emissão"
                  name="eticket_code"
                  defaultValue={item.request_ticket_air_selected.eticket_code}
                  onChange={({ target: { value } }) => {
                    clearTimeout(refTimeout.current)
                    refTimeout.current = setTimeout(() => {
                      dispatch(
                        setFullRequest(
                          UpdateETicketCodeRequest({
                            requests: requests.fullRequest,
                            request_ticket_id: item.request_ticket_id,
                            value
                          })
                        )
                      )
                    }, 500)
                  }}
                />
              )}
            </Col>
          </Row>
        }
      >
        <>
          {item.request_ticket_air_selected.request_quote_air_trip
            .request_quote_air_one_way.request_quote_air_id && (
            <QuoteAirItem
              item={
                item.request_ticket_air_selected.request_quote_air_trip
                  .request_quote_air_one_way
              }
            />
          )}
          {item.request_ticket_air_selected.request_quote_air_trip
            .request_quote_air_round_trip.request_quote_air_id && (
            <QuoteAirItem
              item={
                item.request_ticket_air_selected.request_quote_air_trip
                  .request_quote_air_round_trip
              }
            />
          )}
          {item.request_ticket_user_data.map(user => (
            <AirTravelerRow type="flex" key={user.traveler.user_id}>
              <AirTravelerRowName>
                <UserAvatar
                  gender={user.traveler.gender}
                  name={user.traveler.name}
                  size="large"
                  style={{ marginRight: 8 }}
                />
                <Title level={4} style={{ marginBottom: 0 }}>
                  {user.traveler.name}
                </Title>
              </AirTravelerRowName>
              <AirTravelerRowInfo>
                <Text style={{ marginBottom: 0 }}>
                  <b>{`${t('web.user.common.birthdate.title')}: `}</b>
                  {moment
                    .unix(user.traveler.birthdate)
                    .utc()
                    .format('DD/MM/YYYY')}
                </Text>
                <Text style={{ marginBottom: 0 }}>
                  <b>{`${t('web.user.common.vat_number.title')}: `}</b>
                  {user.traveler.vat_number}
                </Text>
              </AirTravelerRowInfo>
            </AirTravelerRow>
          ))}
          {!!request_standard_sharings && (
            <AirTravelerRow>
              {request_standard_sharings
                ?.filter(({ active }) => active)
                .map(request_standard_sharing => (
                  <Text
                    key={request_standard_sharing.request_standard_sharing_id}
                  >
                    <b>{`${t('web.resources.cost-centers.title')}: `}</b>
                    {`${request_standard_sharing.cost_center.code} - ${request_standard_sharing.cost_center.name}`}
                  </Text>
                ))}
            </AirTravelerRow>
          )}

          <Row
            gutter={16}
            style={{
              border: '1px dashed #e8e8e8',
              borderRadius: 4,
              marginTop: 24,
              padding: '12px 16px'
            }}
          >
            <Col span={5}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Text type="secondary">Tarifa:</Text>
                <Title style={{ fontSize: 16, marginTop: 0 }}>
                  {`BRL ${
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value:
                        item.request_ticket_air_selected.request_quote_air_trip
                          .fare_value
                    }).maskedValue
                  }`}
                </Title>
              </div>
            </Col>
            <Col span={5}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Text type="secondary">Taxa:</Text>
                <Title style={{ fontSize: 16, marginTop: 0 }}>
                  {`BRL ${
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value:
                        item.request_ticket_air_selected.request_quote_air_trip
                          .boarding_value
                    }).maskedValue
                  }`}
                </Title>
              </div>
            </Col>
            <Col span={5}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Text type="secondary">DU:</Text>
                <Title style={{ fontSize: 16, marginTop: 0 }}>
                  {`BRL ${
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value:
                        item.request_ticket_air_selected.request_quote_air_trip
                          .service_value
                    }).maskedValue
                  }`}
                </Title>
              </div>
            </Col>
            <Col span={5}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Text type="secondary">FEE:</Text>
                <Title style={{ fontSize: 16, marginTop: 0 }}>
                  {`BRL ${
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value: item.request_ticket_air_selected.fee_value
                    }).maskedValue
                  }`}
                </Title>
              </div>
            </Col>
            <Col span={4}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Text type="secondary">Total:</Text>
                <Title style={{ fontSize: 16, marginTop: 0 }}>
                  {`BRL ${
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value:
                        item.request_ticket_air_selected.request_quote_air_trip
                          .total_air_value
                    }).maskedValue
                  }`}
                </Title>
              </div>
            </Col>
          </Row>
        </>
      </Card>
    </List.Item>
  )
}

export default TicketItem
